import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MoreIcon } from '@/components/icons';
import { CalendarDays, User, Globe, CircleDot, MessageCircle, Eye, ExternalLink, ChevronDown, ChevronUp, MessagesSquare, Pencil, Upload, Trash2, Undo } from 'lucide-react';
import { useProgramme, useDeleteProgramme, useUpdateProgrammeStatus, useInfiniteProgrammeComments } from '@/lib/api/hooks/programme';
import { LoadingMore } from '@/components/ui/LoadingMore';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { ActionDropdown, DeleteModal, PublishModal, UnpublishModal, type DropdownAction } from '@/components/ui';
import ArticleContent from '@/components/ui/ArticleContent';
import { showToast } from '@/lib/toast';

interface ProgrammeMicroSite {
  micrositeTemplateType?: string;
  branding?: {
    websiteLogoUrl?: string;
    websiteColorCode?: string;
    textColorCode?: string;
  };
  headerSection?: {
    headerTitle?: string;
    headerDescription?: string;
    headerImageUrl?: string | null;
  };
  missionTimeLineSection?: {
    missionTimeLineSectionDetails?: unknown[] | null;
  };
  newsFeedSection?: {
    newsFeedSectionDetails?: unknown[];
  };
  footerSection?: {
    footerEmail?: string;
    footerAddress?: string;
  };
}

interface ProgrammeWebsiteFields {
  websiteType?: string | null;
  websiteLink?: string | null;
  isAddWebsite?: boolean;
  microSite?: ProgrammeMicroSite | null;
  coverImageUrl?: string;
  updatedAt?: string;
  totalComments?: number;
}

const ProgrammeDetailPage: React.FC = () => {
  const { id: programmeId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [isCommentsCollapsed, setIsCommentsCollapsed] = useState(false);

  const { data: programmeData, isLoading, error } = useProgramme(programmeId || '');
  const article = programmeData?.data;

  const {
    data: commentsData,
    isLoading: isLoadingComments,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteProgrammeComments(programmeId || '');

  const { mutate: deleteProgramme, isPending: isDeleting } = useDeleteProgramme();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateProgrammeStatus();

  const comments = commentsData?.pages.flatMap((page) => page.data?.data || []) || [];
  const totalComments = commentsData?.pages[0]?.data?.totalOfAllElements || 0;

  const sentinelRef = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage: isFetchingNextPage ?? false,
    fetchNextPage,
  });

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'pm' : 'am';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${day} ${month} at ${displayHours}:${displayMinutes}${ampm}`;
    } catch {
      return dateString;
    }
  };

  const handleEdit = () => {
    if (!programmeId) return;
    navigate(`/admin/programmes/${programmeId}/edit`);
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleStatusUpdateClick = () => {
    if (!article) return;
    setStatusModalOpen(true);
  };

  const confirmDelete = () => {
    if (!programmeId) return;
    deleteProgramme(programmeId, {
      onSuccess: (response) => {
        if (response.success) {
          showToast.success('Programme deleted successfully!');
          setDeleteModalOpen(false);
          navigate('/admin/programmes');
        }
      },
      onError: (err: Error) => {
        showToast.error(err);
      }
    });
  };

  const confirmStatusUpdate = () => {
    if (!article || !programmeId) return;
    const newStatus = article.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

    updateStatus(
      { programmeId, status: newStatus },
      {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success(
              newStatus === 'PUBLISH'
                ? 'Programme published successfully!'
                : 'Programme unpublished successfully!'
            );
            setStatusModalOpen(false);
          }
        },
        onError: (err: Error) => {
          showToast.error(err);
        }
      }
    );
  };

  const getDropdownActions = (): DropdownAction[] => [
    {
      label: 'Edit',
      onClick: (e) => {
        e.stopPropagation();
        handleEdit();
      }
    },
    {
      label: article?.status === 'PUBLISH' ? 'Un-Publish' : 'Publish',
      onClick: (e) => {
        e.stopPropagation();
        handleStatusUpdateClick();
      },
      disabled: isUpdatingStatus
    },
    {
      label: 'Delete',
      onClick: (e) => {
        e.stopPropagation();
        handleDeleteClick();
      },
      variant: 'danger' as const,
      disabled: isDeleting
    }
  ];

  const quickActions = [
    {
      key: 'edit',
      label: 'Edit',
      icon: Pencil,
      onClick: handleEdit,
      className: 'text-gray-700 border-gray-200 hover:bg-gray-50',
      disabled: false,
    },
    {
      key: 'status',
      label: article?.status === 'PUBLISH' ? 'Un-Publish' : 'Publish',
      icon: article?.status === 'PUBLISH' ? Undo : Upload,
      onClick: handleStatusUpdateClick,
      className: 'text-gray-700 border-gray-200 hover:bg-gray-50',
      disabled: isUpdatingStatus,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: Trash2,
      onClick: handleDeleteClick,
      className: 'text-red-600 border-red-200 hover:bg-red-50',
      disabled: isDeleting,
    },
  ];

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-wine-100"></div>
          <p className="mt-4 text-sm text-gray-500">Loading programme...</p>
        </div>
      </div>
    );
  }

  if (!programmeId) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Programme ID missing</h2>
        <button onClick={() => navigate('/admin/programmes')} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Programmes
        </button>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Programme not found</h2>
        <p className="text-sm text-gray-500 mt-2">{error?.message || 'The programme could not be found'}</p>
        <button onClick={() => navigate('/admin/programmes')} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Programmes
        </button>
      </div>
    );
  }

  const isUnpublishing = article.status === 'PUBLISH';
  const articleWithWebsite = article as typeof article & ProgrammeWebsiteFields;
  const authorName = article.createdByUsername || article.createdBy;
  const publishedOn = article.datePublished || article.createdAt;
  const lastUpdated = articleWithWebsite.updatedAt || article.createdAt;
  const coverImageSrc = article.coverImage || articleWithWebsite.coverImageUrl || '';
  const websiteType = articleWithWebsite.websiteType;
  const websiteLink = articleWithWebsite.websiteLink;
  const microSite = articleWithWebsite.microSite;
  const totalCommentCount = articleWithWebsite.totalComments ?? totalComments;

  return (
    <>
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Programme?"
        subtitle="Are you sure you want to delete this programme?"
        itemName={article.title}
        warningText="This action cannot be undone. The programme will be permanently deleted."
        itemType="Programme"
        isLoading={isDeleting}
      />

      {isUnpublishing ? (
        <UnpublishModal
          isOpen={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          itemType="Programme"
          isLoading={isUpdatingStatus}
        />
      ) : (
        <PublishModal
          isOpen={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          title="Publish Programme?"
          subtitle="Are you sure you want to publish this programme?"
          itemName={article.title}
          infoText="It will be displayed on your website."
          itemType="Programme"
          isLoading={isUpdatingStatus}
        />
      )}

      <div className="space-y-6">
        <div className="text-sm text-gray-500">
          <span className="cursor-pointer hover:underline" onClick={() => navigate('/admin/programmes')}>Programmes</span>
          <span className="mx-2">&gt;</span>
          <span className="font-medium text-gray-700">{article.title}</span>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                {coverImageSrc && (
                  <img src={coverImageSrc} alt={article.title} className="w-full md:w-60 h-44 object-cover rounded-xl flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <h1 className="text-3xl font-semibold text-gray-900">{article.title}</h1>
                    <ActionDropdown id={programmeId} actions={getDropdownActions()} />
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{formatDate(publishedOn)}</span>
                    <span className="text-gray-300">|</span>
                    <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" />By {authorName}</span>
                    <span className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium ${article.status === 'PUBLISH' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      <span className={`h-2 w-2 rounded-full ${article.status === 'PUBLISH' ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                      {article.status === 'PUBLISH' ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1.5"><Globe className="w-4 h-4" />Language: <span className="font-medium text-gray-800">English (EN)</span></span>
                  </div>

                    {websiteLink && (
                      <div className="mt-4">
                        <a
                          href={websiteLink || '#'}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#681628] hover:underline"
                        >
                          Visit Programme Website
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        </div>
                    )}
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
              <ArticleContent content={article.content || ''} className="" />
            </div>

            {websiteType === 'MICROSITE' && microSite && (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex flex-col gap-4">
                <div className="flex items-center justify-between gap-4 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">Microsite Details</h3>
                  {websiteLink && (
                    <a
                      href={websiteLink}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="flex-shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-[#681628] hover:underline"
                    >
                      Visit Website
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <div className="grid grid-cols-[auto_1fr] gap-4 items-start">
                  {microSite.headerSection?.headerImageUrl && (
                    <img
                      src={microSite.headerSection.headerImageUrl}
                      alt={microSite.headerSection.headerTitle || 'Microsite header'}
                      className="w-48 h-full object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  <div className="min-w-0 flex flex-col gap-2">
                    <h4 className="text-base font-semibold text-gray-900">
                      {microSite.headerSection?.headerTitle || 'Microsite'}
                    </h4>
                    {microSite.headerSection?.headerDescription && (
                      <p className="text-sm text-gray-600 line-clamp-4">{microSite.headerSection.headerDescription}</p>
                    )}
                        <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="rounded-lg border border-gray-200 p-2">
                    <p className="text-xs text-gray-500">Timeline Items</p>
                    <p className="text-xl font-semibold text-gray-900 mt-1">{microSite.missionTimeLineSection?.missionTimeLineSectionDetails?.length || 0}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-2">
                    <p className="text-xs text-gray-500">News Feed Items</p>
                    <p className="text-xl font-semibold text-gray-900 mt-1">{microSite.newsFeedSection?.newsFeedSectionDetails?.length || 0}</p>
                  </div>
                </div>
                  </div>
                </div>
            
              </div>
            )}

{/* Comments Section */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="p-4 flex justify-between items-center border-b border-gray-200 bg-gray-50 rounded-t-lg">
                <h3 className="text-lg flex items-center gap-2 text-gray-800"><MessagesSquare className="w-5 h-5" /> Comments ({totalComments})</h3>
                <button
                  type="button"
                  onClick={() => setIsCommentsCollapsed((prev) => !prev)}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
                  aria-expanded={!isCommentsCollapsed}
                  aria-label={isCommentsCollapsed ? 'Expand comments section' : 'Collapse comments section'}
                >
                  {isCommentsCollapsed ? 'Expand' : 'Collapse'}
                  {isCommentsCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </button>
              </div>
              {!isCommentsCollapsed && (
              <div className="space-y-4 p-6">
                {isLoadingComments ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">Loading comments...</p>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No comments yet</p>
                  </div>
                ) : (
                  <>
                    {comments.map((comment: { id: string; name: string; email: string; comment: string }) => (
                      <div key={comment.id} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-gray-800">{comment.name}</p>
                            <p className="text-xs text-gray-500">{comment.email}</p>
                          </div>
                          <button className="text-gray-500 hover:text-gray-800"><MoreIcon /></button>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">{comment.comment}</p>
                      </div>
                    ))}
                    <div ref={sentinelRef}>
                      {isFetchingNextPage && <LoadingMore />}
                      {!hasNextPage && comments.length > 0 && totalComments > 10 && (
                        <div className="text-center py-4 text-sm text-gray-500">
                          No more comments
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              )}
            </div>
          </div>

          <aside className="xl:sticky xl:top-24 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <h3 className="text-lg font-semibold text-gray-900">Programme Details</h3>

              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><User className="w-4 h-4" />Author</span>
                  <span className="text-gray-900 font-medium text-right">{authorName}</span>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><CalendarDays className="w-4 h-4" />Published On</span>
                  <span className="text-gray-900 font-medium text-right">{formatDate(publishedOn)}</span>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><CalendarDays className="w-4 h-4" />Last Updated</span>
                  <span className="text-gray-900 font-medium text-right">{formatDate(lastUpdated)}</span>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><Globe className="w-4 h-4" />Language</span>
                  <span className="text-gray-900 font-medium text-right">English (EN)</span>
                </div>
                <div className="flex justify-between gap-3 items-start">
                  <span className="text-gray-500 inline-flex items-center gap-2"><CircleDot className="w-4 h-4" />Status</span>
                  <span className={`inline-flex items-center gap-1 text-xs font-medium ${article.status === 'PUBLISH' ? 'text-emerald-700' : 'text-amber-700'}`}>
                    <span className={`h-2 w-2 rounded-full ${article.status === 'PUBLISH' ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                    {article.status === 'PUBLISH' ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <h3 className="text-lg font-semibold text-gray-900">Engagement Overview</h3>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500 inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" />Views</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">{article.totalViews || 0}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500 inline-flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" />Comments</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">{totalCommentCount}</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <h3 className="text-lg font-semibold text-gray-900">Highlights</h3>
              <div className="mt-4 space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={article.allowComments}
                    disabled
                    className="checkbox-wine"
                  />
                  <span className="ml-3 text-sm text-gray-700">Allow Comments</span>
                </label>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {quickActions.map((action) => {
                  const ActionIcon = action.icon;

                  return (
                    <button
                      key={action.key}
                      type="button"
                      onClick={action.onClick}
                      disabled={action.disabled}
                      className={`inline-flex items-center justify-center gap-2.5 px-4 py-3 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${action.className}`}
                    >
                      <ActionIcon className="w-4 h-4" />
                      {action.label}
                    </button>
                  );
                })}
              </div>
            </div>


          </aside>
        </div>
      </div>
    </>
  );
};

export default ProgrammeDetailPage;
